/**
 * Every fetch() Response is either READ or RELEASED — never dropped.
 *
 * gbuff, 2026-10-02 (Dan's Brave on Linux): a Response body holds a browser
 * resource until it is read or cancelled — in Chromium, a 2 MB shared-memory
 * data pipe (`/dev/shm/.org.chromium.Chromium.*`) plus a file descriptor in
 * the tab's renderer process — and an unread body was NOT released by garbage
 * collection in practice. A renderer's soft descriptor limit is 1024. Pollers
 * that dropped error replies (`if (!res.ok) return`) leaked one pipe per
 * error: 965 Kalshi 502s left 967 leaked 2 MB segments (1.9 GB) and the
 * process pinned at 1007/1024 descriptors. At the cap the tab can open
 * nothing new — no fetch leaves the browser, the shared feed workers in its
 * process accept no port, DevTools cannot attach — so every g1 tab of the
 * origin wedges with it. Measured: 10 unread error replies → +10 retained
 * segments; read or cancelled → +0.
 *
 * `bufferErrorResponse` is what `apiFetch` returns for an error reply (so no
 * caller of it can leak one); `releaseBody` is for raw fetch() sites that do
 * not read a body on some path.
 */

/** Release a body nobody will read. Idempotent; never throws. A locked body
 *  (a reader holds it) is left to that reader. */
export function releaseBody(res: Response | null | undefined): void {
  if (!res || res.bodyUsed || !res.body || res.body.locked) return;
  try {
    void res.body.cancel().catch(() => {});
  } catch {
    /* already closing */
  }
}

const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

/** A 2xx reply is returned untouched (streams included). Any other reply is
 *  read into memory and handed back as an equivalent in-memory Response —
 *  same status, statusText, headers, url, body bytes — so its network pipe is
 *  closed before any caller sees it, and a caller that ignores the body leaks
 *  nothing. An abort while reading rejects as the caller's own read would. */
export async function bufferErrorResponse(res: Response): Promise<Response> {
  if (res.ok || !res.body || res.bodyUsed || res.status < 200 || res.status > 599) return res;
  let bytes: ArrayBuffer;
  try {
    bytes = await res.arrayBuffer();
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    releaseBody(res);
    bytes = new ArrayBuffer(0);
  }
  const body = NULL_BODY_STATUSES.has(res.status) || bytes.byteLength === 0 ? null : bytes;
  const copy = new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers });
  // The constructor cannot set these; callers may read them off the original.
  for (const key of ['url', 'redirected', 'type'] as const) {
    try {
      Object.defineProperty(copy, key, { value: res[key], configurable: true, enumerable: true });
    } catch {
      /* non-configurable in this runtime — the copy keeps its own value */
    }
  }
  return copy;
}
